import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import { format } from "date-fns";
import {
  ArrowDownAZ,
  ArrowDownNarrowWide,
  ArrowDownUp,
  ArrowUpAZ,
  Check,
  Filter,
  MoreVertical,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  getExplorationsByGradeLevel,
  getExplorationsByPathway,
  getExplorationsByRotation,
  getExplorationsBySession,
  getExplorationsByTeacher,
} from "@/utils/explorations/filters";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Input } from "@/components/ui/Input/Input";
import { sortExplorations } from "@/utils/explorations/sorting";
import { getPageItems } from "@/utils/pagination";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { Link, useSearchParams } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command/Command";
import { parseCsv, writeCsv } from "@/utils/csv";

const RESULTS_PER_PAGE_OPTIONS = [8, 12, 36, 72, 120, 240, 480] as const;

const Explorations = ({
  data,
  onEdit,
  onEnroll,
  archived = false,
}: {
  data: {
    explorations: any[];
    classes: any[];
    rotations: any[];
    sessions: any[];
    teachers: any[];
  };
  onEdit: (explorationId: string) => void;
  onEnroll: (explorationId: string) => void;
  archived?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize, setPageSize] = useState<number>(12);
  const [page, setPage] = useState<number>(1);

  // Rotations Filter
  const rotationOptions = useMemo(
    () => [
      ...data.rotations
        .slice()
        .sort((a, b) => +new Date(b.date) - +new Date(a.date))
        .map((r) => ({
          value: r._id,
          label: format(r.date, "MMMM dd, yyyy"),
        })),
    ],
    [data.rotations],
  );

  const toggleRotation = (rotationId: string) => {
    setSearchParams((prev) => {
      const current = parseCsv(prev.get("rot"));
      const next = current.includes(rotationId)
        ? current.filter((id) => id !== rotationId)
        : [...current, rotationId];

      const sp = new URLSearchParams(prev);
      writeCsv(sp, "rot", next);
      return sp;
    });
  };

  const clearRotationFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("rot");
      return sp;
    });
  };

  const selectedRotationIds = useMemo(
    () => parseCsv(searchParams.get("rot")),
    [searchParams],
  );

  const filteredByRotation = useMemo(() => {
    if (selectedRotationIds.length === 0) return data.explorations;

    return getExplorationsByRotation(
      data.explorations,
      data.sessions,
      selectedRotationIds,
    );
  }, [selectedRotationIds, data.explorations, data.sessions]);

  // Sessions Filter
  const sessionOptions = useMemo(() => {
    const names = new Set(data.sessions.map((s) => s.name.trim()));
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [data.sessions]);

  const toggleSession = (sessionName: string) => {
    setSearchParams((prev) => {
      const current = parseCsv(prev.get("sesh"));
      const next = current.includes(sessionName)
        ? current.filter((id) => id !== sessionName)
        : [...current, sessionName];

      const sp = new URLSearchParams(prev);
      writeCsv(sp, "sesh", next);
      return sp;
    });
  };

  const clearSessionsFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("sesh");
      return sp;
    });
  };

  const selectedSessionNames = useMemo(
    () => parseCsv(searchParams.get("sesh")),
    [searchParams],
  );

  const filteredBySession = useMemo(() => {
    if (selectedSessionNames.length === 0) return filteredByRotation;

    return getExplorationsBySession(
      filteredByRotation,
      data.sessions,
      selectedSessionNames,
    );
  }, [filteredByRotation, data.sessions, selectedSessionNames]);

  // Teachers Filter
  const teacherOptions = useMemo(() => {
    const teacherIds = new Set<string>();

    for (const e of data.explorations) {
      if (e.teacher) teacherIds.add(e.teacher);
      for (const id of e.coTeachers ?? []) teacherIds.add(id);
    }

    const byId = new Map(data.teachers.map((t) => [t._id, t.name]));

    return Array.from(teacherIds).map((id) => ({
      value: id,
      label: byId.get(id) ?? `Unknown (${id.slice(-6)})`,
    }));
  }, [data.explorations, data.teachers]);

  const toggleTeacher = (teacherId: string) => {
    setSearchParams((prev) => {
      const current = parseCsv(prev.get("teach"));
      const next = current.includes(teacherId)
        ? current.filter((id) => id !== teacherId)
        : [...current, teacherId];

      const sp = new URLSearchParams(prev);
      writeCsv(sp, "teach", next);
      return sp;
    });
  };

  const clearTeachersFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("teach");
      return sp;
    });
  };

  const selectedTeacherIds = useMemo(
    () => parseCsv(searchParams.get("teach")),
    [searchParams],
  );

  const filteredByTeacher = useMemo(() => {
    if (selectedTeacherIds.length === 0) return filteredBySession;

    return getExplorationsByTeacher(filteredBySession, selectedTeacherIds);
  }, [filteredBySession, selectedTeacherIds]);

  // Grades Filter
  const gradeOptions = useMemo(() => {
    const grades = new Set<number>();

    for (const e of data.explorations) {
      for (const g of e.gradeLevels ?? []) {
        grades.add(g);
      }
    }

    return Array.from(grades)
      .sort((a, b) => a - b)
      .map((g) => ({ value: g, label: g }));
  }, [data.explorations]);

  const toggleGrade = (grade: number) => {
    setSearchParams((prev) => {
      const current = parseCsv(prev.get("grade"));
      const next = current.includes(String(grade))
        ? current.filter((id) => id !== String(grade))
        : [...current, String(grade)];

      const sp = new URLSearchParams(prev);
      writeCsv(sp, "grade", next);
      return sp;
    });
  };

  const clearGradesFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("grade");
      return sp;
    });
  };

  const selectedGrades = useMemo(
    () => parseCsv(searchParams.get("grade")),
    [searchParams],
  );

  const filteredByGrade = useMemo(() => {
    if (selectedGrades.length === 0) return filteredByTeacher;

    return getExplorationsByGradeLevel(
      filteredByTeacher,
      selectedGrades.map((g) => Number(g)),
    );
  }, [filteredByTeacher, selectedGrades]);

  // Pathways Filter
  const pathwayOptions = useMemo(() => {
    const pathways = new Set<string>();

    for (const e of data.explorations) {
      if (e.pathway) pathways.add(e.pathway);
    }

    return Array.from(pathways)
      .sort((a, b) => a.localeCompare(b))
      .map((p) => ({ value: p, label: p }));
  }, [data.explorations]);

  const togglePathway = (pathway: string) => {
    setSearchParams((prev) => {
      const current = parseCsv(prev.get("path"));
      const next = current.includes(pathway)
        ? current.filter((id) => id !== pathway)
        : [...current, pathway];

      const sp = new URLSearchParams(prev);
      writeCsv(sp, "path", next);
      return sp;
    });
  };

  const clearPathwaysFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("path");
      return sp;
    });
  };

  const selectedPathways = useMemo(
    () => parseCsv(searchParams.get("path")),
    [searchParams],
  );

  const filteredByPathway = useMemo(() => {
    if (selectedPathways.length === 0) return filteredByGrade;

    return getExplorationsByPathway(filteredByGrade, selectedPathways);
  }, [filteredByGrade, selectedPathways]);

  // Open Only Filter
  const toggleOpenOnly = () => {
    setSearchParams((prev) => {
      const current = prev.get("open");

      const sp = new URLSearchParams(prev);

      if (current === "true") {
        sp.delete("open");
      } else {
        sp.set("open", "true");
      }
      return sp;
    });
  };

  const openOnly = useMemo(() => searchParams.get("open"), [searchParams]);

  const filteredByOpenOnly = useMemo(() => {
    if (openOnly !== "true") return filteredByPathway;

    return filteredByPathway.filter((e) => {
      const enrolled = Number(e.enrolled ?? 0);
      const max = Number(e.maxEnrollment ?? 0);
      return enrolled < max;
    });
  }, [filteredByPathway, searchParams]);

  // Search Filter
  const search = searchParams.get("q") ?? "";

  const setSearch = (value: string) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      const trimmed = value;

      if (!trimmed) {
        sp.delete("q");
      } else {
        sp.set("q", trimmed);
      }

      return sp;
    });
  };

  const filteredBySearch = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return filteredByOpenOnly;

    return filteredByOpenOnly.filter((e) => {
      const name = (e.name ?? "").toLowerCase();
      const desc = (e.description ?? "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [filteredByOpenOnly, search]);

  // Overall Filtering
  const clearAllFilters = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      sp.delete("rot");
      sp.delete("sesh");
      sp.delete("teach");
      sp.delete("grade");
      sp.delete("path");
      sp.delete("open");
      sp.delete("q");

      return sp;
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      selectedRotationIds.length > 0 ||
      selectedSessionNames.length > 0 ||
      selectedTeacherIds.length > 0 ||
      selectedGrades.length > 0 ||
      selectedPathways.length > 0 ||
      openOnly
    );
  }, [
    selectedRotationIds,
    selectedSessionNames,
    selectedTeacherIds,
    selectedGrades,
    selectedPathways,
    openOnly,
  ]);

  const totalCount = data.explorations.length;
  const filteredCount = filteredBySearch.length;

  // Sorting
  const sortKey = useMemo(() => {
    return searchParams.get("sortK");
  }, [searchParams]);

  const sortValue = useMemo(() => {
    const value = searchParams.get("sortV");
    return value === "asc" || value === "desc" ? value : null;
  }, [searchParams]);

  const sortedExplorations = useMemo(() => {
    if (!sortKey || !sortValue) return filteredBySearch;

    return sortExplorations(
      filteredBySearch,
      {
        name: sortKey === "name" ? sortValue : "",
        rotationDate: sortKey === "rotationDate" ? sortValue : "",
        session: sortKey === "session" ? sortValue : "",
        teacher: sortKey === "teacher" ? sortValue : "",
      },
      {
        sessions: data.sessions,
        rotations: data.rotations,
        teachers: data.teachers,
      },
    );
  }, [
    filteredBySearch,
    sortKey,
    sortValue,
    data.sessions,
    data.rotations,
    data.teachers,
  ]);

  const toggleSort = (key: "name" | "rotationDate" | "session" | "teacher") => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      const currentK = sp.get("sortK");
      const currentV = sp.get("sortV");

      let nextV = "asc";

      if (currentK === key) {
        nextV = currentV === "asc" ? "desc" : "asc";
      }

      sp.set("sortK", key);
      sp.set("sortV", nextV);

      return sp;
    });
  };

  const clearSorting = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("sortK");
      sp.delete("sortV");
      return sp;
    });
  };

  const hasActiveSorting = useMemo(() => {
    return !!(sortKey && sortValue);
  }, [sortKey, sortValue]);

  // Attendance Dropdown
  const classesForExploration = useMemo(() => {
    const byExploration = new Map<string, { _id: string; date: Date }[]>();

    for (const c of data.classes) {
      if (!c.exploration) continue;
      const list = byExploration.get(c.exploration) ?? [];
      list.push({ _id: c._id, date: new Date(c.date) });
      byExploration.set(c.exploration, list);
    }

    for (const [, list] of byExploration) {
      list.sort((a, b) => +a.date - +b.date);
    }

    return byExploration;
  }, [data.classes]);

  // Pagination
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredCount / pageSize));
  }, [filteredCount, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [
    selectedRotationIds,
    selectedSessionNames,
    selectedTeacherIds,
    selectedGrades,
    selectedPathways,
    openOnly,
    search,
    sortKey,
    sortValue,
    pageSize,
  ]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paginatedExplorations = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedExplorations.slice(start, start + pageSize);
  }, [sortedExplorations, page, pageSize]);

  const showPagination = totalPages > 1;

  const pageItems = useMemo(
    () => getPageItems(page, totalPages),
    [page, totalPages],
  );

  return (
    <VStack gap="1rem">
      <HStack justify="space-between" align="center" wrap>
        <HStack align="center" gap="1rem">
          <HStack align="center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="small">
                  <Filter /> Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Dates</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {rotationOptions.map((r) => (
                      <DropdownMenuCheckboxItem
                        key={r.value}
                        checked={selectedRotationIds.includes(r.value)}
                        onCheckedChange={() => toggleRotation(r.value)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {r.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        clearRotationFilter();
                      }}
                      disabled={selectedRotationIds.length === 0}
                    >
                      Clear
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Sessions</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {sessionOptions.map((s) => (
                      <DropdownMenuCheckboxItem
                        key={s.value}
                        checked={selectedSessionNames.includes(s.value)}
                        onCheckedChange={() => toggleSession(s.value)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {s.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        clearSessionsFilter();
                      }}
                      disabled={selectedSessionNames.length === 0}
                    >
                      Clear
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Teachers</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent style={{ padding: 0 }}>
                    <Command
                      filter={(itemValue, search) => {
                        const value = itemValue.toLowerCase();
                        const query = search.toLowerCase();
                        if (!query) return 1;
                        return value.includes(query) ? 1 : 0;
                      }}
                      style={{ padding: "var(--space-xxs)" }}
                    >
                      <CommandInput
                        placeholder="Search teachers..."
                        wrapperStyles={{ border: "none" }}
                      />
                      <DropdownMenuSeparator />
                      <CommandList>
                        <CommandEmpty>No teachers found.</CommandEmpty>
                        <CommandGroup style={{ padding: 0 }}>
                          {teacherOptions.map((opt) => {
                            const isSelected = selectedTeacherIds.includes(
                              opt.value,
                            );

                            return (
                              <CommandItem
                                key={opt.value}
                                value={opt.label}
                                onSelect={() => {
                                  toggleTeacher(opt.value);
                                }}
                              >
                                <HStack
                                  align="center"
                                  justify="space-between"
                                  style={{ width: "100%" }}
                                >
                                  {opt.label}
                                  {isSelected && (
                                    <Check
                                      style={{
                                        width: "1rem",
                                        height: "1rem",
                                        color: "var(--secondary-foreground)",
                                      }}
                                    />
                                  )}
                                </HStack>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          clearTeachersFilter();
                        }}
                        disabled={selectedTeacherIds.length === 0}
                      >
                        Clear
                      </DropdownMenuItem>
                    </Command>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Grades</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {gradeOptions.map((g) => (
                      <DropdownMenuCheckboxItem
                        key={g.value}
                        checked={selectedGrades.includes(String(g.value))}
                        onCheckedChange={() => toggleGrade(g.value)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {g.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        clearGradesFilter();
                      }}
                      disabled={selectedGrades.length === 0}
                    >
                      Clear
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Pathways</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {pathwayOptions.map((p) => (
                      <DropdownMenuCheckboxItem
                        key={p.value}
                        checked={selectedPathways.includes(p.value)}
                        onCheckedChange={() => togglePathway(p.value)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {p.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        clearPathwaysFilter();
                      }}
                      disabled={selectedPathways.length === 0}
                    >
                      Clear
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuCheckboxItem
                  checked={openOnly === "true"}
                  onCheckedChange={toggleOpenOnly}
                  onSelect={(e) => e.preventDefault()}
                >
                  Open Only
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    clearAllFilters();
                  }}
                  disabled={!hasActiveFilters}
                >
                  Clear All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <p
              style={{
                color: "var(--muted-foreground)",
              }}
            >
              {filteredCount} of {totalCount} explorations
            </p>
          </HStack>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="small">
                <ArrowDownNarrowWide /> Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggleSort("name");
                }}
              >
                Name{" "}
                {sortKey === "name" && sortValue === "asc" ? (
                  <ArrowDownAZ />
                ) : sortKey === "name" && sortValue === "desc" ? (
                  <ArrowUpAZ />
                ) : (
                  <ArrowDownUp />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggleSort("rotationDate");
                }}
              >
                Date{" "}
                {sortKey === "rotationDate" && sortValue === "asc" ? (
                  <ArrowDownAZ />
                ) : sortKey === "rotationDate" && sortValue === "desc" ? (
                  <ArrowUpAZ />
                ) : (
                  <ArrowDownUp />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggleSort("session");
                }}
              >
                Session{" "}
                {sortKey === "session" && sortValue === "asc" ? (
                  <ArrowDownAZ />
                ) : sortKey === "session" && sortValue === "desc" ? (
                  <ArrowUpAZ />
                ) : (
                  <ArrowDownUp />
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  toggleSort("teacher");
                }}
              >
                Teacher{" "}
                {sortKey === "teacher" && sortValue === "asc" ? (
                  <ArrowDownAZ />
                ) : sortKey === "teacher" && sortValue === "desc" ? (
                  <ArrowUpAZ />
                ) : (
                  <ArrowDownUp />
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!hasActiveSorting}
                onSelect={(e) => {
                  e.preventDefault();
                  clearSorting();
                }}
              >
                Clear Sorting
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </HStack>
        <HStack>
          <div style={{ width: "240px", maxWidth: "100%" }}>
            <Input
              placeholder="Search explorations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => setPageSize(Number(v))}
          >
            <SelectTrigger style={{ width: "fit-content" }}>
              <SelectValue placeholder="Select results per page" />
            </SelectTrigger>
            <SelectContent>
              {RESULTS_PER_PAGE_OPTIONS.map((r) => (
                <SelectItem key={r} value={String(r)}>
                  {r} results/page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      </HStack>
      <HStack gap="1rem" style={{ flexWrap: "wrap" }}>
        {paginatedExplorations.map((e) => {
          const classList = classesForExploration.get(e._id) ?? [];

          return (
            <DashboardCard
              key={e._id}
              style={{
                flex: "1 1 calc(20% - 1rem)",
                flexWrap: "nowrap",
              }}
            >
              <VStack
                justify="space-between"
                gap="1rem"
                style={{ height: "100%" }}
              >
                <VStack gap="0.25rem">
                  <HStack justify="space-between">
                    <p
                      style={{
                        fontSize: "var(--text-md)",
                        lineHeight: "var(--line-height-md)",
                        fontWeight: "var(--font-bold)",
                      }}
                    >
                      {e.name}
                    </p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {!archived && (
                          <>
                            <DropdownMenuItem onSelect={() => onEdit(e._id)}>
                              Edit Exploration
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onEnroll(e._id)}>
                              Enroll Students
                            </DropdownMenuItem>
                          </>
                        )}
                        {classList.length === 1 && (
                          <DropdownMenuItem
                            asChild
                            style={{ textDecoration: "none" }}
                          >
                            <Link to={`/attendance/${classList[0]._id}`}>
                              Attendance
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {classList.length > 1 && (
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              Attendance
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {classList.map((c) => (
                                <DropdownMenuItem
                                  key={c._id}
                                  asChild
                                  style={{ textDecoration: "none" }}
                                >
                                  <Link to={`/attendance/${c._id}`}>
                                    {format(c.date, "MMMM dd, yyyy")}
                                  </Link>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </HStack>
                  <p
                    style={{
                      fontSize: "var(--text-xs)",
                      lineHeight: "var(--line-height-xs)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    {e.description}
                  </p>
                </VStack>
                <VStack gap="0.125rem">
                  <p>
                    Date:{" "}
                    {format(
                      data.rotations.find(
                        (r) =>
                          r._id ===
                          data.sessions.find((s) => s._id === e.session)
                            ?.rotation,
                      )?.date!,
                      "MMMM dd, yyyy",
                    )}
                  </p>
                  <p>
                    Session:{" "}
                    {data.sessions.find((s) => s._id === e.session)?.name}
                  </p>
                  <p>
                    Teacher:{" "}
                    {data.teachers.find((t) => t._id === e.teacher)?.name}
                  </p>
                  {e.coTeachers.length > 0 && (
                    <p>
                      Co-Teachers:{" "}
                      {data.teachers
                        .filter((t) => e.coTeachers.includes(t._id))
                        .map((t) => t.name)
                        .join(", ")}
                    </p>
                  )}
                  <p>Grade Levels: {e.gradeLevels.join(", ")}</p>
                  <p>Pathway: {e.pathway}</p>
                  <p>Room: {e.room}</p>
                  <p>
                    {e.enrolled} / {e.maxEnrollment} enrolled
                  </p>
                </VStack>
              </VStack>
            </DashboardCard>
          );
        })}
      </HStack>
      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              />
            </PaginationItem>

            {pageItems.map((item, idx) => {
              if (item === "ellipsis") {
                return (
                  <PaginationItem key={`ellipsis-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem key={item}>
                  <PaginationButton
                    isActive={item === page}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </PaginationButton>
                </PaginationItem>
              );
            })}

            <PaginationItem>
              <PaginationNext
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </VStack>
  );
};

export { Explorations };
